import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import SEOMeta from '@/components/feature/SEOMeta';
import StructuredData from '@/components/feature/StructuredData';
import { categories, getMoviesByCategory, type CategoryType, type Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const currentCategory: CategoryType = (['movie', 'tv', 'variety', 'anime'].includes(category || '') ? category : 'movie') as CategoryType;

  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'update'>('rating');

  const categoryInfo = categories.find(c => c.key === currentCategory);

  const allMovies = useMemo(() => {
    let movies = getMoviesByCategory(currentCategory);

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      movies = movies.filter(m =>
        m.title.toLowerCase().includes(q) ||
        m.englishTitle.toLowerCase().includes(q) ||
        m.tags.some(t => t.toLowerCase().includes(q)) ||
        m.actors.some(a => a.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'rating':
        return [...movies].sort((a, b) => b.rating - a.rating);
      case 'year':
        return [...movies].sort((a, b) => b.year - a.year);
      case 'update':
        return [...movies].sort((a, b) => b.updateDate.localeCompare(a.updateDate));
      default:
        return movies;
    }
  }, [currentCategory, searchQuery, sortBy]);

  const siteUrl = 'https://777cinema.com';
  const pageUrl = `${siteUrl}/category/${currentCategory}`;
  const catLabel = categoryInfo?.label || '影视';

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: catLabel, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `777影院 - ${catLabel}分类`,
      url: pageUrl,
      description: categoryInfo?.description || `777影院${catLabel}分类页面`,
      isPartOf: { '@type': 'WebSite', name: '777影院', url: siteUrl },
    },
  ];

  return (
    <>
      <SEOMeta
        title={`777影院${catLabel} - ${catLabel}高清在线观看 | 777影院免费超清`}
        description={`777影院${catLabel}频道，提供最新最热${catLabel}免费在线观看，超清画质，流畅不卡顿，${categoryInfo?.description || ''}`}
        keywords={`777影院${catLabel},${catLabel}在线观看,${catLabel}免费,${catLabel}超清,777影院免费,不卡,流畅`}
        canonicalUrl={pageUrl}
        ogType="website"
      />
      <StructuredData data={structuredData} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-16">
          <div className="w-full px-4 md:px-6">
            <Breadcrumb items={[{ label: catLabel }]} />
          </div>

          <section className="py-6 md:py-10">
            <div className="w-full px-4 md:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    <i className={`${categoryInfo?.icon || 'ri-film-line'} text-primary-500 mr-2`}></i>
                    {catLabel}
                  </h1>
                  {searchQuery && (
                    <p className="text-sm text-foreground-500 mt-1">
                      搜索 &ldquo;{searchQuery}&rdquo; 的结果，共 {allMovies.length} 部
                    </p>
                  )}
                  {!searchQuery && (
                    <p className="text-sm text-foreground-500 mt-1">{categoryInfo?.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {(['rating', 'year', 'update'] as const).map((s) => (
                    <button
                      key={s}
                      onClick={() => setSortBy(s)}
                      className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap ${
                        sortBy === s
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                      }`}
                    >
                      {s === 'rating' ? '评分最高' : s === 'year' ? '最新上映' : '最近更新'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex items-center gap-2 mb-8 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      currentCategory === cat.key
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.label}
                  </Link>
                ))}
              </div>

              {allMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
                  {allMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-2xl">
                    <i className="ri-search-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">未找到相关影视内容</p>
                  <Link
                    to={`/category/${currentCategory}`}
                    className="inline-block mt-4 text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors"
                  >
                    清除搜索
                  </Link>
                </div>
              )}
            </div>
          </section>
        </div>

        <Footer />
      </div>
    </>
  );
}